import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-24 lg:py-32">
        <h1 className="font-serif text-4xl tracking-tight">Invalid link</h1>
        <p className="text-sm text-muted-foreground mt-4">This reset link is missing its token. Please request a new one.</p>
        <Link to="/forgot-password" className="inline-block mt-6 text-sm text-foreground hover:text-primary transition-colors">Request a new link →</Link>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-24 lg:py-32">
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ New password</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight font-light">Choose it.</h1>

      {done ? (
        <div data-testid="reset-success" className="mt-12 border border-border rounded-sm p-8 bg-card">
          <CheckCircle2 className="w-7 h-7 text-green-600" />
          <div className="font-serif text-2xl mt-4">All set</div>
          <p className="text-sm text-muted-foreground mt-3">Your password has been updated. Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 space-y-6">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">New password</label>
            <input
              data-testid="reset-password-input"
              type="password" required
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 text-base transition-colors"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Confirm password</label>
            <input
              data-testid="reset-confirm-input"
              type="password" required
              value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 text-base transition-colors"
            />
          </div>
          {error && <div data-testid="reset-error" className="text-sm text-destructive">{error}</div>}
          <button
            data-testid="reset-submit"
            disabled={loading}
            className="btn-primary w-full py-3.5 text-sm tracking-wide rounded-sm disabled:opacity-60"
          >
            {loading ? "Updating…" : "Reset password"}
          </button>
        </form>
      )}
    </div>
  );
}
